import { useRef, useState } from "react";
import { Loader2, Paperclip, Send, X } from "lucide-react";

const MAX_MB = 50;

export default function ChatInput({ enviando, onEnviarTexto, onEnviarMidia, desabilitado, motivoDesabilitado }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const ref = useRef(null);
  const fileRef = useRef(null);

  const podeEnviar = !desabilitado && !enviando && (texto.trim().length > 0 || arquivo);

  async function handleEnviar() {
    if (!podeEnviar) return;
    const txt = texto.trim();
    const file = arquivo;
    setErro(null);
    setTexto("");
    setArquivo(null);
    if (ref.current) ref.current.style.height = "auto";
    ref.current?.focus();
    try {
      if (file) await onEnviarMidia(file, txt);
      else await onEnviarTexto(txt);
    } catch (err) {
      setErro(err?.response?.data?.error?.message ?? err?.response?.data?.error ?? err?.message ?? "Erro ao enviar mensagem.");
      setTexto(txt);
      setArquivo(file);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  }

  function handleArquivo(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setErro(`Arquivo acima de ${MAX_MB} MB.`);
      return;
    }
    setErro(null);
    setArquivo(f);
    ref.current?.focus();
  }

  return (
    <div className="border-t border-white/[0.06] bg-base flex-shrink-0">
      {desabilitado && motivoDesabilitado && (
        <div className="px-4 py-2 bg-warning-500/10 border-b border-warning-500/20 text-xs text-warning-400" role="status">
          {motivoDesabilitado}
        </div>
      )}
      {erro && (
        <div className="px-4 py-1.5 bg-danger-500/10 border-b border-danger-500/20 text-xs text-danger-400" role="alert">
          {typeof erro === "string" ? erro : "Erro ao enviar mensagem."}
        </div>
      )}
      {arquivo && (
        <div className="px-3 pt-2 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 text-xs text-ink-2 bg-surface-2 border border-white/[0.08] rounded-lg px-2.5 py-1 max-w-full">
            <Paperclip className="w-3.5 h-3.5 text-brand-300 flex-shrink-0" />
            <span className="truncate">{arquivo.name}</span>
            <button
              type="button"
              onClick={() => setArquivo(null)}
              aria-label="Remover anexo"
              className="text-ink-4 hover:text-ink-1 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        </div>
      )}

      <div className="flex items-end gap-2 px-3 py-2">
        <input ref={fileRef} type="file" className="hidden" onChange={handleArquivo} aria-hidden="true" tabIndex={-1} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={desabilitado || enviando}
          aria-label="Anexar arquivo"
          title="Anexar arquivo"
          className="mb-0.5 flex-shrink-0 p-2 rounded-xl text-ink-3 hover:text-ink-1 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <Paperclip className="w-4 h-4" />
        </button>

        <textarea
          ref={ref}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={desabilitado || enviando}
          placeholder={arquivo ? "Legenda (opcional)…" : "Escreva uma mensagem… (Enter envia, Shift+Enter quebra linha)"}
          aria-label="Campo de mensagem"
          rows={1}
          className="flex-1 resize-none bg-surface-2 border border-white/[0.08] rounded-xl px-3 py-2 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors leading-relaxed max-h-32 overflow-y-auto scrollbar-thin disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ minHeight: "2.25rem" }}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
          }}
        />

        <button
          type="button"
          onClick={handleEnviar}
          disabled={!podeEnviar}
          aria-label="Enviar mensagem"
          className="mb-0.5 flex-shrink-0 p-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
